/**
 * Design System Tokens - Web App
 * Adapted from mobile app design system to maintain consistency
 */

import { palette } from "@greenbackapp/ui";

// Color Tokens - Monochrome design system
export const colorTokens = {
  light: {
    brand: {
      primary: palette.green,       /* Greenback brand green */
      secondary: palette.green600,  /* slightly darker for secondary use */
      accent: palette.green,
    },
    semantic: {
      success: palette.success,
      error: "#000000",
      negative: "#DC2626",
      warning: "#666666",
      info: "#333333",
    },
    neutral: {
      white: "#FFFFFF",
      black: "#000000",
      grey: {
        50: "#FAFAFA",
        100: "#F5F5F5",
        200: "#EEEEEE",
        300: "#E0E0E0",
        400: "#BDBDBD",
        500: "#9E9E9E",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121",
      },
    },
    text: {
      primary: "#000000",
      secondary: "#666666",
      disabled: "#BDBDBD",
      inverse: "#FFFFFF",
      onBrand: "#080C14",   /* palette.ink — dark text on brand green */
    },
    background: {
      default: "#FFFFFF",
      paper: "#FFFFFF",
      subtle: "#FAFAFA",
    },
    border: {
      default: "#E0E0E0",
      subtle: "#F5F5F5",
    }
  },
  dark: {
    brand: {
      primary: palette.green,       /* same brand green in dark mode */
      secondary: "#22D88E",         /* slightly lighter for dark mode secondary */
      accent: palette.green,
    },
    semantic: {
      success: palette.success,
      error: "#FFFFFF",
      negative: "#f87171",
      warning: "#CCCCCC",
      info: "#CCCCCC",
    },
    neutral: {
      white: "#FFFFFF",
      black: "#000000",
      grey: {
        50: "#0A0A0A",
        100: "#121212",
        200: "#1E1E1E",
        300: "#2A2A2A",
        400: "#3A3A3A",
        500: "#4A4A4A",
        600: "#606060",
        700: "#808080",
        800: "#A0A0A0",
        900: "#C0C0C0",
      },
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#B0B0B0",
      disabled: "#5C5C5C",
      inverse: "#000000",
      onBrand: "#080C14",   /* palette.ink — dark text on brand green */
    },
    background: {
      default: "#000000",
      paper: "#0A0A0A",
      subtle: "#121212",
    },
    border: {
      default: "#222222",
      subtle: "#1A1A1A",
    }
  },
};

// Typography Tokens - Figtree font family
export const typographyTokens = {
  fontFamily: {
    primary: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    light: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    medium: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    semiBold: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    bold: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  },

  fontSize: {
    caption: "12px",
    body: "14px",
    label: "14px",
    subtitle: "16px",
    title: "18px",
    heading: "22px",
    display: "28px",
    // Legacy sizes
    xs: "12px",
    sm: "14px",
    base: "16px",
    lg: "18px",
    xl: "20px",
    "2xl": "24px",
    "3xl": "30px",
  },

  fontWeight: {
    light: "300",
    normal: "400",
    medium: "500",
    semibold: "600",
    bold: "700",
  },

  lineHeight: {
    caption: "16px",
    body: "20px",
    label: "18px",
    subtitle: "22px",
    title: "24px",
    heading: "28px",
    display: "34px",
    tight: "1.25",
    normal: "1.5",
    relaxed: "1.625",
  },
};

// Spacing Tokens - Matching mobile app spacing
export const spacingTokens = {
  px: "1px",
  0: "0px",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "28px",
  8: "32px",
  9: "36px",
  10: "40px",
  12: "48px",
  16: "64px",
  20: "80px",
  24: "96px",
  // Legacy naming
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "20px",
  xxl: "24px",
  xxxl: "32px",
};

// Border Radius Tokens - Consistent design matching mobile app
export const borderRadiusTokens = {
  none: "0px",        // Truly sharp elements (nav items, table cells)
  xs: "4px",          // Subtle rounding (badges, chips)
  sm: "8px",          // Small elements (buttons, inputs)
  md: "12px",         // Cards, modals, sections
  lg: "16px",         // Large containers, hero sections
  xl: "20px",         // Extra large containers
  full: "9999px",     // Circular elements (avatars, badges)
};

// Shape Tokens - Component-specific border radius matching mobile app
export const shapeTokens = {
  cornerRadius: "12px",       // Default for cards, modals, containers
  buttonBorderRadius: "8px",  // Rounded buttons
  inputBorderRadius: "8px",   // Standard for inputs
  avatarBorderRadius: "50%",  // Circular avatars
};

// Navigation Tokens - Professional nav styling
export const navTokens = {
  activeIndicator: {
    width: "3px",
    color: "currentColor",
  },
  itemPadding: "12px",
  itemBorderRadius: "0px",
};


// Shadow Tokens - Minimal shadows
export const shadowTokens = {
  none: "none",
  sm: "0 1px 2px rgba(0, 0, 0, 0.08)",
  md: "0 2px 4px rgba(0, 0, 0, 0.12)",
  lg: "0 4px 8px rgba(0, 0, 0, 0.16)",
};

// Animation Tokens
export const animationTokens = {
  duration: {
    shortest: "100ms",
    short: "200ms",
    standard: "250ms",
    complex: "300ms",
  },
  easing: {
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
  },
};

// Micro-Animation Tokens - Subtle motion for premium UX
export const microAnimationTokens = {
  // Shimmer effect for icons/highlights
  shimmer: {
    duration: '2s',
    keyframes: {
      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
      '50%': { opacity: 0.7, transform: 'scale(1.1)' },
    },
  },
  // Pulsing glow effect
  pulse: {
    duration: '2s',
    spread: {
      min: '20px',
      max: '30px',
    },
  },
  // Floating motion
  float: {
    duration: '3s',
    distance: '4px',
    easing: 'ease-in-out',
  },
  // Gradient background shift
  gradientShift: {
    duration: '8s',
    positions: ['0%', '50%', '100%'],
  },
  // Particle drift for decorative elements
  drift: {
    duration: '6s',
    distance: {
      x: '10px',
      y: '15px',
    },
    easing: 'ease-in-out',
  },
  // Entrance animations
  entrance: {
    slideUp: {
      duration: '0.3s',
      distance: '20px',
    },
    fadeIn: {
      duration: '0.3s',
    },
    scaleIn: {
      duration: '0.2s',
      scale: { start: 0.95, end: 1 },
    },
  },
  // Reduced motion preference
  reducedMotion: {
    duration: '0.01ms',
    iteration: 1,
  },
};

// Focus Tokens - WCAG 2.1 AA compliant focus indicators
export const focusTokens = {
  ring: {
    width: "2px",
    offset: "2px",
    style: "solid",
  },
  color: {
    light: "#000000",  // High contrast on light backgrounds
    dark: "#FFFFFF",   // High contrast on dark backgrounds
  },
};

// Semantic Color Tokens - For status indicators and data trends
export const semanticColorTokens = {
  light: {
    positive: "#047857",      // Green - gains, income, under budget
    negative: "#DC2626",      // Red - losses, expenses, over budget
    warning: "#F59E0B",       // Amber - near budget, caution
    info: "#374151",          // Charcoal - informational
    positiveLight: "#4ade80", // Lighter green for backgrounds/charts
    negativeLight: "#f87171", // Lighter red for backgrounds/charts
    // Status badge background tints (used in TableCell StatusBadge)
    positiveBg: "#E8F5E9",    // Light green tint — positive status chip
    negativeBg: "#FFF5F5",    // Light red tint — negative status chip (4.5:1 with negative text)
    warningBg:  "#FFF8E1",    // Light amber tint — warning status chip
    // Budget-specific
    budgetGood: "#34C759",    // Under budget
    budgetWarning: "#FF9500", // Near budget (90%+)
    budgetOver: "#DC2626",    // Over budget
    // Category backgrounds (pastel)
    categoryFood: "#FFEBEE",
    categoryGrocery: "#E8F5E9",
    categoryTransport: "#E3F2FD",
    categoryShopping: "#FFF3E0",
    categoryEntertainment: "#F3E5F5",
    categoryHealth: "#FFFDE7",
    categoryDefault: "#F5F5F5",
  },
  dark: {
    positive: "#4ade80",      // Brighter green for dark backgrounds
    negative: "#f87171",      // Brighter red for dark backgrounds
    warning: "#FBBF24",       // Brighter amber
    info: "#9CA3AF",          // Neutral gray - informational
    positiveLight: "#059669", // Darker green for contrast
    negativeLight: "#DC2626", // Darker red for contrast
    // Status badge background tints (used in TableCell StatusBadge)
    positiveBg: "#1D3D1D",    // Dark green tint — positive status chip
    negativeBg: "#3D2020",    // Dark red tint — negative status chip
    warningBg:  "#3D3D1D",    // Dark amber tint — warning status chip
    // Budget-specific
    budgetGood: "#34C759",
    budgetWarning: "#FF9500",
    budgetOver: "#FF3B30",
    // Category backgrounds (dark variants)
    categoryFood: "#3D2020",
    categoryGrocery: "#1D3D1D",
    categoryTransport: "#1D2D3D",
    categoryShopping: "#3D2D1D",
    categoryEntertainment: "#2D1D3D",
    categoryHealth: "#3D3D1D",
    categoryDefault: "#2A2A2A",
  },
};

// Chart Color Tokens - 10-color palette for data visualization
export const chartColorTokens = {
  palette: [
    "#6366f1", // Indigo
    "#8b5cf6", // Violet
    "#ec4899", // Pink
    "#f43f5e", // Rose
    "#f97316", // Orange
    "#eab308", // Yellow
    "#22c55e", // Green
    "#14b8a6", // Teal
    "#94A3B8", // Slate
    "#F472B6", // Rose Pink
  ],
  // Axis and grid colors
  axis: {
    light: {
      line: "#D4D4D4",
      tick: "#808080",
      grid: "#EEEEEE",
      label: "#111111",
    },
    dark: {
      line: "#3D3D3D",
      tick: "#A0A0A0",
      grid: "#2A2A2A",
      label: "#E5E5E5",
    },
  },
};

// Gradient Tokens - Hero section backgrounds
export const gradientTokens = {
  light: {
    netWorth: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    investments: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0d4f6e 100%)",
    budget: "linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 50%, #1e3a5f 100%)",
    cashFlow: "linear-gradient(135deg, #1a1a2e 0%, #1e3a5f 50%, #0f3460 100%)",
    accent: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
  },
  dark: {
    netWorth: "linear-gradient(135deg, #0a0a14 0%, #0d1520 50%, #081828 100%)",
    investments: "linear-gradient(135deg, #080c14 0%, #0f1e2f 50%, #081f2e 100%)",
    budget: "linear-gradient(135deg, #0a0a14 0%, #160d24 50%, #0f1e2f 100%)",
    cashFlow: "linear-gradient(135deg, #0a0a14 0%, #0f1e2f 50%, #081828 100%)",
    accent: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
  },
};

// Glassmorphism Tokens - Frosted glass effects for premium UI elements
export const glassmorphismTokens = {
  // Background layers with translucent gradients
  background: {
    light: {
      primary: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.08) 100%)',
      secondary: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(139, 92, 246, 0.06) 100%)',
      subtle: 'rgba(255, 255, 255, 0.6)',
    },
    dark: {
      primary: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
      secondary: 'linear-gradient(135deg, rgba(236, 72, 153, 0.12) 0%, rgba(139, 92, 246, 0.08) 100%)',
      subtle: 'rgba(30, 30, 30, 0.6)',
    },
  },
  // Backdrop blur values
  blur: {
    sm: '8px',
    md: '12px',
    lg: '20px',
    xl: '30px',
  },
  // Border styling for glass surfaces
  border: {
    light: 'rgba(99, 102, 241, 0.2)',
    dark: 'rgba(255, 255, 255, 0.1)',
    width: '1px',
  },
  // Decorative gradient orbs for visual interest
  orb: {
    // Indigo/Violet orb
    primary: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    // Violet/Pink orb  
    secondary: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
    // Emerald/Teal orb
    tertiary: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
    // Size configurations
    size: {
      sm: '100px',
      md: '200px',
      lg: '300px',
    },
    blur: '60px',
    opacity: {
      light: 0.4,
      dark: 0.3,
    },
  },
  // Glow effects for interactive elements
  glow: {
    primary: {
      light: 'rgba(99, 102, 241, 0.3)',
      dark: 'rgba(99, 102, 241, 0.4)',
    },
    secondary: {
      light: 'rgba(139, 92, 246, 0.3)',
      dark: 'rgba(139, 92, 246, 0.4)',
    },
    // Glow spread values
    spread: {
      sm: '10px',
      md: '20px',
      lg: '30px',
    },
  },
};

// Breakpoint Tokens - Mobile-first responsive design
export const breakpointTokens = {
  mobile: {
    small: "320px",   // iPhone SE
    medium: "375px",  // iPhone 12/13/14
    large: "428px",   // iPhone 14 Pro Max
  },
  tablet: {
    small: "768px",   // iPad portrait
    large: "1024px",  // iPad landscape
  },
  desktop: {
    small: "1280px",
    medium: "1440px",
    large: "1920px",
  },
};

// Media Query Helpers - Use in styled-components
export const mediaQueries = {
  // Mobile-first approach: target screens UP TO these sizes
  mobile: {
    small: `@media (max-width: ${breakpointTokens.mobile.small})`,
    medium: `@media (max-width: ${breakpointTokens.mobile.medium})`,
    large: `@media (max-width: ${breakpointTokens.mobile.large})`,
  },
  tablet: {
    small: `@media (max-width: ${breakpointTokens.tablet.small})`,
    large: `@media (max-width: ${breakpointTokens.tablet.large})`,
  },
  desktop: {
    small: `@media (max-width: ${breakpointTokens.desktop.small})`,
  },
  // Common breakpoints used in existing code
  upToMobile: `@media (max-width: 480px)`,   // Legacy support
  upToTabletSmall: `@media (max-width: 600px)`, // Legacy support
  upToTablet: `@media (max-width: ${breakpointTokens.tablet.small})`,
  upToDesktop: `@media (max-width: ${breakpointTokens.tablet.large})`,
  // Min-width (mobile-first ascending) — apply styles AT and ABOVE these sizes
  tabletUp: `@media (min-width: ${breakpointTokens.tablet.small})`,    // ≥768px
  desktopUp: `@media (min-width: ${breakpointTokens.desktop.small})`,  // ≥1280px
};

// Grid System Tokens - 12-column responsive layout for data-heavy accounting views
export const gridSystemTokens = {
  // Column configuration
  columns: {
    count: 12,
    // Column width calculation helper
    // Usage: width = `${(span / gridSystemTokens.columns.count) * 100}%`
  },

  // Gutter (gap between columns)
  gutter: {
    mobile: spacingTokens[3],     // 12px on mobile
    tablet: spacingTokens[4],     // 16px on tablet
    desktop: spacingTokens[6],    // 24px on desktop
  },

  // Row gap (gap between rows)
  rowGap: {
    mobile: spacingTokens[3],     // 12px on mobile
    tablet: spacingTokens[4],     // 16px on tablet
    desktop: spacingTokens[5],    // 20px on desktop
  },

  // Container max-widths per breakpoint
  container: {
    maxWidth: {
      mobile: "100%",             // Full width on mobile
      tablet: "720px",            // Contained on tablet
      desktop: {
        small: "1200px",          // Standard desktop
        medium: "1360px",         // Large desktop
        large: "1680px",          // Ultra-wide
      },
    },
    // Container padding (outer margins)
    padding: {
      mobile: spacingTokens[4],   // 16px
      tablet: spacingTokens[6],   // 24px
      desktop: spacingTokens[8],  // 32px
    },
  },

  // Common column span configurations for accounting layouts
  layouts: {
    // Full width
    full: { span: 12 },
    // Half width
    half: { span: 6 },
    // Thirds
    oneThird: { span: 4 },
    twoThirds: { span: 8 },
    // Quarters
    oneQuarter: { span: 3 },
    threeQuarters: { span: 9 },
    // Sidebar + main content patterns
    sidebar: { span: 3 },
    main: { span: 9 },
    // Narrow sidebar for navigation
    narrowSidebar: { span: 2 },
    wideMain: { span: 10 },
  },

  // Responsive column overrides
  responsive: {
    // Stack to full width on mobile
    stackOnMobile: {
      mobile: 12,
      tablet: null, // Uses default span
      desktop: null,
    },
    // Sidebar collapses on tablet
    collapseSidebar: {
      mobile: 12,
      tablet: 4,
      desktop: 3,
    },
    // Dynamic main content
    dynamicMain: {
      mobile: 12,
      tablet: 8,
      desktop: 9,
    },
  },

  // Offset (push columns to the right)
  offset: {
    auto: "auto",
    // Pre-calculated offsets
    0: "0",
    1: `${(1 / 12) * 100}%`,
    2: `${(2 / 12) * 100}%`,
    3: `${(3 / 12) * 100}%`,
    4: `${(4 / 12) * 100}%`,
    5: `${(5 / 12) * 100}%`,
    6: `${(6 / 12) * 100}%`,
  },

  // Order utilities for responsive reordering
  order: {
    first: -1,
    last: 999,
    none: 0,
  },

  // Alignment utilities
  alignment: {
    // Justify content (horizontal)
    justify: {
      start: "flex-start",
      center: "center",
      end: "flex-end",
      between: "space-between",
      around: "space-around",
      evenly: "space-evenly",
    },
    // Align items (vertical)
    items: {
      start: "flex-start",
      center: "center",
      end: "flex-end",
      stretch: "stretch",
      baseline: "baseline",
    },
  },

  // Pre-calculated column widths (percentage)
  columnWidth: {
    1: "8.333333%",
    2: "16.666667%",
    3: "25%",
    4: "33.333333%",
    5: "41.666667%",
    6: "50%",
    7: "58.333333%",
    8: "66.666667%",
    9: "75%",
    10: "83.333333%",
    11: "91.666667%",
    12: "100%",
  },
};

// Z-Index Tokens - Comprehensive layering system for UI elements
export const zIndexTokens = {
  // Base layer - Default page content
  base: 0,

  // Below base - For elements that should appear behind content
  negative: {
    deep: -100,      // Absolutely hidden background elements
    behind: -1,      // Slightly behind normal flow
  },

  // Content layers - Elements within the normal page flow
  content: {
    default: 1,      // Normal stacking context
    raised: 10,      // Slightly elevated content (cards with hover)
    floating: 20,    // Floating action buttons, sticky elements
  },

  // Navigation layer - Site navigation elements
  navigation: {
    sidebar: 100,    // Side navigation panel
    header: 110,     // Top navigation header
    breadcrumb: 105, // Breadcrumb navigation
  },

  // Overlay layers - Elements that overlay the main content
  overlay: {
    backdrop: 200,   // Modal/drawer backdrop
    scrim: 210,      // Dark overlay behind modals
  },

  // Dropdown layer - Menus and dropdowns
  dropdown: {
    menu: 300,       // Dropdown menus
    select: 310,     // Select/autocomplete dropdowns
    datePicker: 320, // Date picker calendars
    colorPicker: 320, // Color picker popups
  },

  // Popover layer - Popovers and tooltips
  popover: {
    default: 400,    // Standard popovers
    nested: 410,     // Nested popovers
  },

  // Tooltip layer - Tooltips should appear above most elements
  tooltip: {
    default: 500,    // Standard tooltips
    highPriority: 510, // Important tooltips that need to be visible
  },

  // Modal layer - Modal dialogs
  modal: {
    backdrop: 600,   // Modal backdrop
    content: 610,    // Modal content
    nested: 620,     // Nested modals (modal-in-modal)
    confirmDialog: 630, // Confirmation dialogs (above other modals)
  },

  // Toast/Notification layer - Non-blocking alerts
  toast: {
    container: 700,  // Toast container
    item: 710,       // Individual toast items
  },

  // Top-level layers - Maximum priority elements
  topLevel: {
    loading: 800,    // Full-screen loading overlays
    alert: 900,      // Critical system alerts
    debug: 9999,     // Development debug overlays
  },

  // Common aliases for convenience
  stickyHeader: 110,
  drawer: 200,
  menuAlias: 300,
  popoverAlias: 400,
  tooltipAlias: 500,
  modalAlias: 610,
  toastAlias: 700,
  loadingOverlay: 800,
};

// Component Tokens
export const componentTokens = {
  card: {
    padding: spacingTokens.md,
    margin: spacingTokens.md,
    borderRadius: borderRadiusTokens.md, // 12px - rounded design
    shadow: shadowTokens.sm,
    borderWidth: "1px",
    header: {
      padding: {
        default: spacingTokens.lg,
        compact: spacingTokens.md,
      },
      borderBottom: {
        width: "1px",
        style: "solid",
      },
      title: {
        fontSize: {
          default: typographyTokens.fontSize.title,
          compact: typographyTokens.fontSize.body,
        },
        fontWeight: typographyTokens.fontWeight.semibold,
        fontFamily: typographyTokens.fontFamily.semiBold,
        lineHeight: typographyTokens.lineHeight.tight,
      },
      subtitle: {
        fontSize: typographyTokens.fontSize.caption,
        fontWeight: typographyTokens.fontWeight.normal,
        lineHeight: typographyTokens.lineHeight.normal,
        marginTop: spacingTokens.xs,
      },
    },
  },
  button: {
    padding: {
      horizontal: spacingTokens.lg,
      vertical: spacingTokens.sm,
    },
    borderRadius: borderRadiusTokens.sm, // 8px - slightly less rounded for buttons
    borderWidth: "1px",
    fontSize: typographyTokens.fontSize.label,
    fontWeight: typographyTokens.fontWeight.medium,
    // Interaction states
    hover: {
      opacity: "0.85",
      transform: "translateY(-1px)",
    },
    active: {
      opacity: "1",
      transform: "scale(0.98)",
    },
    focus: {
      ringWidth: focusTokens.ring.width,
      ringOffset: focusTokens.ring.offset,
    },
    disabled: {
      opacity: "0.5",
      cursor: "not-allowed",
    },
    transition: `all ${animationTokens.duration.short} ${animationTokens.easing.easeInOut}`,
  },
  input: {
    padding: spacingTokens.sm,
    borderRadius: borderRadiusTokens.md, // 12px - rounded design
    borderWidth: "1px",
    fontSize: typographyTokens.fontSize.body,
  },
};

// Data Table Tokens - Professional sheets-like data table UX
export const dataTableTokens = {
  // Row heights for different density modes
  rowHeight: {
    compact: "32px",    // Dense data display
    default: "44px",    // Standard (WCAG touch target)
    comfortable: "56px", // Spacious display
  },

  // Cell padding based on content type
  cellPadding: {
    horizontal: {
      compact: spacingTokens[2],   // 8px
      default: spacingTokens[3],   // 12px
      comfortable: spacingTokens[4], // 16px
    },
    vertical: {
      compact: spacingTokens[1],   // 4px
      default: spacingTokens[2],   // 8px
      comfortable: spacingTokens[3], // 12px
    },
  },

  // Zebra striping colors for alternating rows
  zebra: {
    light: {
      even: colorTokens.light.background.default,  // #FFFFFF
      odd: colorTokens.light.background.subtle,    // #FAFAFA
    },
    dark: {
      even: "#1A1A1A",   // Softer dark gray (was #121212)
      odd: "#222222",    // Slightly lighter gray (was #2A2A2A)
    },
  },

  // Header styling
  header: {
    height: "48px",
    background: {
      light: colorTokens.light.neutral.grey[100], // #F5F5F5
      dark: "#1E1E1E",   // Softer header background (was #212121)
    },
    borderBottom: {
      width: "2px",
      light: colorTokens.light.border.default,    // #E0E0E0
      dark: "#2A2A2A",      // Reduced border contrast (was #3D3D3D)
    },
    sticky: {
      zIndex: 10,
      shadow: {
        light: "0 2px 4px rgba(0, 0, 0, 0.08)",
        dark: "0 2px 4px rgba(0, 0, 0, 0.20)",  // Reduced shadow intensity (was 0.32)
      },
    },
    typography: {
      fontSize: typographyTokens.fontSize.caption, // 12px
      fontWeight: typographyTokens.fontWeight.semibold, // 600
      textTransform: "uppercase" as const,
      letterSpacing: "0.5px",
    },
  },

  // Row and cell borders
  border: {
    cell: {
      width: "1px",
      style: "solid" as const,
      light: colorTokens.light.border.subtle,     // #F5F5F5
      dark: "#252525",       // Softer cell borders (was #2E2E2E)
    },
    row: {
      width: "1px",
      style: "solid" as const,
      light: colorTokens.light.border.default,    // #E0E0E0
      dark: "#2A2A2A",      // Softer row borders (was #3D3D3D)
    },
    outer: {
      width: "1px",
      style: "solid" as const,
      light: colorTokens.light.border.default,
      dark: "#2A2A2A",      // Softer outer borders (was #3D3D3D)
    },
  },

  // Row hover and selection states
  hover: {
    light: colorTokens.light.neutral.grey[100],   // #F5F5F5
    dark: "#282828",     // Softer hover color (was #2E2E2E)
    transition: `background-color ${animationTokens.duration.shortest} ${animationTokens.easing.easeInOut}`,
  },

  selection: {
    single: {
      light: colorTokens.light.neutral.grey[200], // #EEEEEE
      dark: "#2D2D2D",   // Softer selection color (was #3D3D3D)
    },
    multi: {
      light: "rgba(0, 0, 0, 0.08)",               // Subtle selection
      dark: "rgba(255, 255, 255, 0.05)",  // Reduced multi-select opacity (was 0.08)
    },
    checkbox: {
      size: "18px",
      padding: spacingTokens[3],
    },
  },

  // Focus states for keyboard navigation
  focus: {
    cell: {
      outline: {
        width: focusTokens.ring.width,            // 2px
        offset: `-${focusTokens.ring.offset}`,    // -2px (inset)
        light: focusTokens.color.light,           // #000000
        dark: focusTokens.color.dark,             // #FFFFFF
      },
    },
    row: {
      outline: {
        width: focusTokens.ring.width,
        offset: focusTokens.ring.offset,
        light: focusTokens.color.light,
        dark: focusTokens.color.dark,
      },
    },
  },

  // Column alignment for different data types
  alignment: {
    text: "left" as const,
    number: "right" as const,
    currency: "right" as const,
    date: "center" as const,
    action: "center" as const,
    checkbox: "center" as const,
  },

  // Resizable column handles
  resizeHandle: {
    width: "4px",
    hoverWidth: "6px",
    light: colorTokens.light.border.default,
    dark: colorTokens.dark.border.default,
    activeLight: colorTokens.light.brand.primary,
    activeDark: colorTokens.dark.brand.primary,
  },

  // Sort indicators in header
  sortIndicator: {
    size: "12px",
    spacing: spacingTokens[1],
    active: {
      light: colorTokens.light.text.primary,
      dark: colorTokens.dark.text.primary,
    },
    inactive: {
      light: colorTokens.light.text.disabled,
      dark: colorTokens.dark.text.disabled,
    },
  },

  // Empty state styling
  emptyState: {
    minHeight: "200px",
    padding: spacingTokens[8],
    iconSize: "48px",
    typography: {
      title: typographyTokens.fontSize.title,
      message: typographyTokens.fontSize.body,
    },
  },

  // Loading state
  loading: {
    skeleton: {
      height: "16px",
      borderRadius: borderRadiusTokens.none,
      light: colorTokens.light.neutral.grey[200],
      dark: colorTokens.dark.neutral.grey[300],
    },
    overlay: {
      light: "rgba(255, 255, 255, 0.8)",
      dark: "rgba(18, 18, 18, 0.8)",
    },
  },
};

// Input State Tokens - Comprehensive styling for form input states
export const inputStateTokens = {
  // Default state - neutral, ready for interaction
  default: {
    background: {
      light: colorTokens.light.background.default,  // #FFFFFF
      dark: colorTokens.dark.background.paper,      // #1E1E1E
    },
    border: {
      color: {
        light: colorTokens.light.border.default,    // #E0E0E0
        dark: colorTokens.dark.border.default,      // #3D3D3D
      },
      width: "1px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.primary,        // #000000
      dark: colorTokens.dark.text.primary,          // #FFFFFF
    },
    placeholder: {
      light: colorTokens.light.text.disabled,       // #BDBDBD
      dark: colorTokens.dark.text.disabled,         // #5C5C5C
    },
    label: {
      light: colorTokens.light.text.secondary,      // #666666
      dark: colorTokens.dark.text.secondary,        // #A0A0A0
    },
  },

  // Focus state - actively being edited
  focus: {
    background: {
      light: colorTokens.light.background.default,
      dark: colorTokens.dark.background.paper,
    },
    border: {
      color: {
        light: colorTokens.light.brand.primary,     // #000000
        dark: colorTokens.dark.brand.primary,       // #FFFFFF
      },
      width: "2px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.primary,
      dark: colorTokens.dark.text.primary,
    },
    label: {
      light: colorTokens.light.brand.primary,
      dark: colorTokens.dark.brand.primary,
    },
    ring: {
      width: focusTokens.ring.width,                // 2px
      offset: focusTokens.ring.offset,              // 2px
      color: {
        light: focusTokens.color.light,             // #000000
        dark: focusTokens.color.dark,               // #FFFFFF
      },
    },
    shadow: {
      light: "0 0 0 3px rgba(0, 0, 0, 0.1)",
      dark: "0 0 0 3px rgba(255, 255, 255, 0.1)",
    },
    transition: `border-color ${animationTokens.duration.short} ${animationTokens.easing.easeInOut}, 
                 box-shadow ${animationTokens.duration.short} ${animationTokens.easing.easeInOut}`,
  },

  // Error state - validation failed
  error: {
    background: {
      light: "#FEF2F2",                             // Light red tint
      dark: "#2D1A1A",                              // Dark red tint
    },
    border: {
      color: {
        light: semanticColorTokens.light.negative,  // #DC2626
        dark: semanticColorTokens.dark.negative,    // #f87171
      },
      width: "2px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.primary,
      dark: colorTokens.dark.text.primary,
    },
    label: {
      light: semanticColorTokens.light.negative,
      dark: semanticColorTokens.dark.negative,
    },
    message: {
      fontSize: typographyTokens.fontSize.caption,  // 12px
      fontWeight: typographyTokens.fontWeight.medium,
      color: {
        light: semanticColorTokens.light.negative,
        dark: semanticColorTokens.dark.negative,
      },
      marginTop: spacingTokens[1],                  // 4px
    },
    icon: {
      size: "16px",
      color: {
        light: semanticColorTokens.light.negative,
        dark: semanticColorTokens.dark.negative,
      },
    },
  },

  // Success state - validation passed
  success: {
    background: {
      light: "#F0FDF4",                             // Light green tint
      dark: "#1A2D1A",                              // Dark green tint
    },
    border: {
      color: {
        light: semanticColorTokens.light.positive,  // #059669
        dark: semanticColorTokens.dark.positive,    // #4ade80
      },
      width: "2px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.primary,
      dark: colorTokens.dark.text.primary,
    },
    label: {
      light: semanticColorTokens.light.positive,
      dark: semanticColorTokens.dark.positive,
    },
    message: {
      fontSize: typographyTokens.fontSize.caption,
      fontWeight: typographyTokens.fontWeight.medium,
      color: {
        light: semanticColorTokens.light.positive,
        dark: semanticColorTokens.dark.positive,
      },
      marginTop: spacingTokens[1],
    },
    icon: {
      size: "16px",
      color: {
        light: semanticColorTokens.light.positive,
        dark: semanticColorTokens.dark.positive,
      },
    },
  },

  // Warning state - caution required
  warning: {
    background: {
      light: "#FFFBEB",                             // Light amber tint
      dark: "#2D2A1A",                              // Dark amber tint
    },
    border: {
      color: {
        light: semanticColorTokens.light.warning,   // #F59E0B
        dark: semanticColorTokens.dark.warning,     // #FBBF24
      },
      width: "2px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.primary,
      dark: colorTokens.dark.text.primary,
    },
    label: {
      light: semanticColorTokens.light.warning,
      dark: semanticColorTokens.dark.warning,
    },
    message: {
      fontSize: typographyTokens.fontSize.caption,
      fontWeight: typographyTokens.fontWeight.medium,
      color: {
        light: semanticColorTokens.light.warning,
        dark: semanticColorTokens.dark.warning,
      },
      marginTop: spacingTokens[1],
    },
    icon: {
      size: "16px",
      color: {
        light: semanticColorTokens.light.warning,
        dark: semanticColorTokens.dark.warning,
      },
    },
  },

  // Disabled state - not interactive
  disabled: {
    background: {
      light: colorTokens.light.neutral.grey[100],   // #F5F5F5
      dark: colorTokens.dark.neutral.grey[100],     // #212121
    },
    border: {
      color: {
        light: colorTokens.light.border.subtle,     // #F5F5F5
        dark: colorTokens.dark.border.subtle,       // #2E2E2E
      },
      width: "1px",
      style: "solid" as const,
    },
    text: {
      light: colorTokens.light.text.disabled,       // #BDBDBD
      dark: colorTokens.dark.text.disabled,         // #5C5C5C
    },
    placeholder: {
      light: colorTokens.light.text.disabled,
      dark: colorTokens.dark.text.disabled,
    },
    label: {
      light: colorTokens.light.text.disabled,
      dark: colorTokens.dark.text.disabled,
    },
    cursor: "not-allowed",
    opacity: "0.6",
  },

  // Readonly state - visible but not editable
  readonly: {
    background: {
      light: colorTokens.light.background.subtle,   // #FAFAFA
      dark: colorTokens.dark.background.subtle,     // #2A2A2A
    },
    border: {
      color: {
        light: colorTokens.light.border.subtle,     // #F5F5F5
        dark: colorTokens.dark.border.subtle,       // #2E2E2E
      },
      width: "1px",
      style: "dashed" as const,                     // Dashed to indicate readonly
    },
    text: {
      light: colorTokens.light.text.primary,        // Full contrast - still readable
      dark: colorTokens.dark.text.primary,
    },
    label: {
      light: colorTokens.light.text.secondary,
      dark: colorTokens.dark.text.secondary,
    },
    cursor: "default",
  },

  // Hover state enhancements
  hover: {
    default: {
      border: {
        light: colorTokens.light.neutral.grey[500], // #9E9E9E
        dark: colorTokens.dark.neutral.grey[500],   // #5C5C5C
      },
      transition: `border-color ${animationTokens.duration.shortest} ${animationTokens.easing.easeInOut}`,
    },
  },

  // Shared sizing and spacing
  sizing: {
    minHeight: {
      small: "32px",
      default: "40px",
      large: "48px",
    },
    padding: {
      horizontal: {
        small: spacingTokens[2],                    // 8px
        default: spacingTokens[3],                  // 12px
        large: spacingTokens[4],                    // 16px
      },
      vertical: {
        small: spacingTokens[1],                    // 4px
        default: spacingTokens[2],                  // 8px
        large: spacingTokens[3],                    // 12px
      },
    },
    iconSpacing: spacingTokens[2],                  // 8px from edge
  },

  // Typography for inputs
  typography: {
    fontSize: {
      small: typographyTokens.fontSize.caption,     // 12px
      default: typographyTokens.fontSize.body,      // 14px
      large: typographyTokens.fontSize.subtitle,    // 16px
    },
    fontWeight: typographyTokens.fontWeight.normal,
    lineHeight: typographyTokens.lineHeight.normal,
    labelFontSize: typographyTokens.fontSize.caption,
    labelFontWeight: typographyTokens.fontWeight.medium,
  },

  // Accessibility
  accessibility: {
    minTouchTarget: "44px",                         // WCAG 2.1 minimum
    focusVisible: {
      outline: `${focusTokens.ring.width} ${focusTokens.ring.style}`,
      outlineOffset: focusTokens.ring.offset,
    },
  },
};

// Interaction Tokens - Hover, active, selection, and press states
export const interactionTokens = {
  // Hover states - for all interactive elements
  hover: {
    background: {
      light: {
        subtle: "rgba(0, 0, 0, 0.04)",           // Subtle background change
        default: colorTokens.light.neutral.grey[100], // #F5F5F5
        emphasized: colorTokens.light.neutral.grey[200], // #EEEEEE
      },
      dark: {
        subtle: "rgba(255, 255, 255, 0.04)",
        default: colorTokens.dark.neutral.grey[200], // #2E2E2E
        emphasized: colorTokens.dark.neutral.grey[300], // #3D3D3D
      },
    },
    text: {
      light: colorTokens.light.brand.primary,     // #000000
      dark: colorTokens.dark.brand.primary,       // #FFFFFF
    },
    border: {
      light: colorTokens.light.neutral.grey[400], // #BDBDBD
      dark: colorTokens.dark.neutral.grey[400],   // #4D4D4D
    },
    opacity: {
      subtle: "0.8",
      default: "0.9",
      full: "1",
    },
    transform: {
      subtle: "translateY(-1px)",
      none: "none",
    },
    transition: `all ${animationTokens.duration.shortest} ${animationTokens.easing.easeInOut}`,
    cursor: "pointer",
  },

  // Active/pressed states
  active: {
    background: {
      light: {
        subtle: "rgba(0, 0, 0, 0.08)",
        default: colorTokens.light.neutral.grey[200], // #EEEEEE
        emphasized: colorTokens.light.neutral.grey[300], // #E0E0E0
      },
      dark: {
        subtle: "rgba(255, 255, 255, 0.08)",
        default: colorTokens.dark.neutral.grey[300], // #3D3D3D
        emphasized: colorTokens.dark.neutral.grey[400], // #4D4D4D
      },
    },
    transform: {
      scale: "scale(0.98)",
      translateY: "translateY(1px)",
      none: "none",
    },
    opacity: "1",
    transition: `transform ${animationTokens.duration.shortest} ${animationTokens.easing.easeOut}`,
  },

  // Selection states - for selectable items (rows, cards, list items)
  selection: {
    single: {
      background: {
        light: colorTokens.light.neutral.grey[200], // #EEEEEE
        dark: colorTokens.dark.neutral.grey[300],   // #3D3D3D
      },
      border: {
        width: "2px",
        color: {
          light: colorTokens.light.brand.primary,   // #000000
          dark: colorTokens.dark.brand.primary,     // #FFFFFF
        },
      },
    },
    multi: {
      background: {
        light: "rgba(0, 0, 0, 0.06)",
        dark: "rgba(255, 255, 255, 0.06)",
      },
      checkbox: {
        size: "20px",
        borderWidth: "2px",
        checked: {
          background: {
            light: colorTokens.light.brand.primary,
            dark: colorTokens.dark.brand.primary,
          },
          checkmark: {
            light: colorTokens.light.text.inverse,
            dark: colorTokens.dark.text.inverse,
          },
        },
        unchecked: {
          background: {
            light: colorTokens.light.background.default,
            dark: colorTokens.dark.background.paper,
          },
          border: {
            light: colorTokens.light.border.default,
            dark: colorTokens.dark.border.default,
          },
        },
      },
    },
    range: {
      background: {
        light: "rgba(0, 0, 0, 0.04)",
        dark: "rgba(255, 255, 255, 0.04)",
      },
      cursor: "crosshair",
    },
    indicator: {
      width: "3px",
      color: {
        light: colorTokens.light.brand.primary,
        dark: colorTokens.dark.brand.primary,
      },
    },
    transition: `background-color ${animationTokens.duration.shortest} ${animationTokens.easing.easeInOut}`,
  },

  // Focus states (keyboard navigation)
  focus: {
    ring: {
      width: focusTokens.ring.width,              // 2px
      offset: focusTokens.ring.offset,            // 2px
      style: "solid" as const,
      color: {
        light: focusTokens.color.light,           // #000000
        dark: focusTokens.color.dark,             // #FFFFFF
      },
    },
    outline: {
      light: `${focusTokens.ring.width} solid ${focusTokens.color.light}`,
      dark: `${focusTokens.ring.width} solid ${focusTokens.color.dark}`,
    },
    shadow: {
      light: `0 0 0 ${focusTokens.ring.offset} ${focusTokens.color.light}`,
      dark: `0 0 0 ${focusTokens.ring.offset} ${focusTokens.color.dark}`,
    },
    visible: {
      outline: {
        width: focusTokens.ring.width,
        offset: focusTokens.ring.offset,
      },
    },
  },

  // Drag and drop interactions
  drag: {
    dragging: {
      opacity: "0.7",
      shadow: {
        light: shadowTokens.lg,
        dark: "0 4px 8px rgba(0, 0, 0, 0.48)",
      },
      cursor: "grabbing",
      scale: "1.02",
      zIndex: 1000,
    },
    dragHandle: {
      cursor: "grab",
      opacity: {
        default: "0.5",
        hover: "1",
      },
      color: {
        light: colorTokens.light.text.secondary,
        dark: colorTokens.dark.text.secondary,
      },
    },
    dropTarget: {
      valid: {
        background: {
          light: "rgba(5, 150, 105, 0.1)",
          dark: "rgba(74, 222, 128, 0.1)",
        },
        border: {
          width: "2px",
          style: "dashed" as const,
          color: {
            light: semanticColorTokens.light.positive,
            dark: semanticColorTokens.dark.positive,
          },
        },
      },
      invalid: {
        background: {
          light: "rgba(220, 38, 38, 0.1)",
          dark: "rgba(248, 113, 113, 0.1)",
        },
        border: {
          width: "2px",
          style: "dashed" as const,
          color: {
            light: semanticColorTokens.light.negative,
            dark: semanticColorTokens.dark.negative,
          },
        },
      },
      transition: `all ${animationTokens.duration.short} ${animationTokens.easing.easeInOut}`,
    },
  },

  // Disabled state
  disabled: {
    opacity: "0.5",
    cursor: "not-allowed",
    pointerEvents: "none" as const,
    filter: "grayscale(0.3)",
  },

  // Loading interaction overlay
  loading: {
    overlay: {
      background: {
        light: "rgba(255, 255, 255, 0.7)",
        dark: "rgba(18, 18, 18, 0.7)",
      },
      cursor: "wait",
    },
    skeleton: {
      base: {
        light: colorTokens.light.neutral.grey[200],
        dark: colorTokens.dark.neutral.grey[200],
      },
      highlight: {
        light: colorTokens.light.neutral.grey[100],
        dark: colorTokens.dark.neutral.grey[300],
      },
      animation: "shimmer 1.5s ease-in-out infinite",
    },
  },

  // Link interaction states
  link: {
    default: {
      color: {
        light: colorTokens.light.brand.primary,
        dark: colorTokens.dark.brand.primary,
      },
      textDecoration: "underline",
    },
    hover: {
      color: {
        light: colorTokens.light.brand.secondary,
        dark: colorTokens.dark.brand.secondary,
      },
      textDecoration: "underline",
    },
    visited: {
      color: {
        light: colorTokens.light.text.secondary,
        dark: colorTokens.dark.text.secondary,
      },
    },
    active: {
      color: {
        light: colorTokens.light.brand.primary,
        dark: colorTokens.dark.brand.primary,
      },
    },
  },

  // Ripple effect
  ripple: {
    color: {
      light: "rgba(0, 0, 0, 0.2)",
      dark: "rgba(255, 255, 255, 0.2)",
    },
    duration: animationTokens.duration.complex,
    timing: animationTokens.easing.easeOut,
  },

  // Touch feedback
  touch: {
    highlight: {
      light: "rgba(0, 0, 0, 0.1)",
      dark: "rgba(255, 255, 255, 0.1)",
    },
    minTapTarget: "44px",
    tapDelay: "0ms",
  },
};

// Chart Tokens - For Recharts and data visualization
export const chartTokens = {
  // Color Palettes
  palette: {
    // Categorical Data (Pie, Bar stacks)
    categorical: [
      "#94A3B8", // Slate
      "#10B981", // Emerald 500
      "#F59E0B", // Amber 500
      "#EF4444", // Red 500
      "#8B5CF6", // Violet 500
      "#EC4899", // Pink 500
      "#2DD4BF", // Teal
      "#F97316", // Orange 500
    ],
    // Sequential Data (Heatmaps)
    sequential: {
      neutral: ["#F5F5F5", "#D4D4D4", "#A0A0A0", "#606060", "#1A1A1A"],
      emerald: ["#ECFDF5", "#A7F3D0", "#34D399", "#059669", "#064E3B"],
    },
  },

  // Specific Data Types
  data: {
    income: semanticColorTokens.light.positive,
    expense: semanticColorTokens.light.negative,
    balance: semanticColorTokens.light.info,
    forecast: semanticColorTokens.light.warning,
  },

  // Chart Grid System
  grid: {
    stroke: {
      light: "#E5E7EB", // Grey 200
      dark: "#374151",  // Grey 700
    },
    strokeDasharray: "3 3",
  },

  // Axis Styling
  axis: {
    stroke: {
      light: "#9CA3AF", // Grey 400
      dark: "#6B7280",  // Grey 500
    },
    tick: {
      fill: {
        light: "#6B7280", // Grey 500
        dark: "#9CA3AF",  // Grey 400
      },
      fontSize: "12px",
      fontFamily: typographyTokens.fontFamily.medium,
    },
  },

  // Tooltip Styling
  tooltip: {
    background: {
      light: "rgba(255, 255, 255, 0.95)",
      dark: "rgba(31, 41, 55, 0.95)", // Grey 800
    },
    border: {
      light: "#E5E7EB", // Grey 200
      dark: "#4B5563",  // Grey 600
    },
    shadow: shadowTokens.lg,
    borderRadius: "4px",
    padding: "8px 12px",
  },

  // Legend Styling
  legend: {
    fontSize: "12px",
    fontFamily: typographyTokens.fontFamily.medium,
    color: {
      light: "#374151", // Grey 700
      dark: "#D1D5DB",  // Grey 300
    },
  },
};